import { useState, useEffect, useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useDarkMode } from "../../../../context/ThemeContext";
import {
  usePointOfInterestStore,
  type PointOfInterest,
} from "../../../../store/admin/pointOfInterest";
import { usePointsInterestList } from "../../../../features/pointsInterest/queries";
import { useDebounce } from "../../../../hooks/useDebounce";
import { qk } from "../../../../lib/queryKeys";
import { useCompaniesStore } from "../../../../store/admin/companiesStore";
import { useAlert } from "../../../../context/AlertContext";
import {
  PointOfInterestModal,
  PointOfInterestDetailsModal,
  PointOfInterestFilters,
  PointOfInterestCard,
  PointOfInterestRowCard,
  usePointOfInterestForm,
  type PointOfInterestFormValues,
} from "./pointOfInterest";
import { useDisableCRUD } from "../../../../store/admin/companiesStore";
import { CRUDTooltip } from "../../../../components/base/CRUDTooltip";
import PaginationControls from "../../../../components/base/PaginationControls";
import { DENSE_LIST } from "../../shared";

export default function PointOfInterestsSection() {
  const { t } = useTranslation("adminSections");
  const { isDarkMode } = useDarkMode();
  const { showAlert } = useAlert();
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [categoryFilter, setCategoryFilter] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedView, setSelectedView] = useState("list");
  const [selectedPOI, setSelectedPOI] = useState<PointOfInterest | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  const [showAddModal, setShowAddModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editPOI, setEditPOI] = useState<PointOfInterest | null>(null);
  const [loadingSubmit, setLoadingSubmit] = useState(false);

  const { createPointOfInterest, updatePointOfInterest, deletePointOfInterest } =
    usePointOfInterestStore();

  const debouncedSearch = useDebounce(searchTerm);
  const filterParams = useMemo(
    () => ({
      page: currentPage,
      limit: itemsPerPage,
      status_id: statusFilter || undefined,
      category_id: categoryFilter || undefined,
      search: debouncedSearch || undefined,
    }),
    [currentPage, itemsPerPage, statusFilter, categoryFilter, debouncedSearch],
  );

  const poiQuery = usePointsInterestList(filterParams);
  const pointsOfInterest = poiQuery.data?.data ?? [];
  const meta = poiQuery.data?.meta;
  const totalPages = meta?.totalPages || 1;
  const loading = poiQuery.isLoading;
  const error = poiQuery.error ? "Error al cargar puntos de interés" : null;

  const queryClient = useQueryClient();
  const companyId = useCompaniesStore((s) => s.companyId);
  const invalidatePOIs = () => {
    queryClient.invalidateQueries({ queryKey: ["pointsInterest"] });
    queryClient.invalidateQueries({ queryKey: qk.counts(companyId) });
  };

  const disableCRUD = useDisableCRUD();

  const {
    form,
    mapCenter,
    markerPosition,
    hasRealChanges,
    handleMarkerPositionChange,
    handleCoordinateChange,
    resetForm,
    preparePOIData,
  } = usePointOfInterestForm({
    isEdit,
    editPOI,
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, categoryFilter, debouncedSearch]);

  const themeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-white",
    cardBorder: isDarkMode ? "border-dark-border" : "border-gray-200",
    buttonHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-100",
    buttonText: isDarkMode ? "text-gray-400" : "text-gray-600",
    buttonTextHover: isDarkMode ? "hover:text-white" : "hover:text-gray-900",
  };

  const handleDetailsClick = (poi: PointOfInterest) => {
    setSelectedPOI(poi);
    setIsModalOpen(true);
  };

  const handleAddNew = () => {
    setShowAddModal(true);
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setIsEdit(false);
    setEditPOI(null);
    setLoadingSubmit(false);
    resetForm();
  };

  const handleEditPOI = useCallback((poi: PointOfInterest) => {
    setEditPOI(poi);
    setIsEdit(true);
    setTimeout(() => {
      setShowAddModal(true);
    }, 0);
  }, []);

  const handleCreatePOISubmit = async (data: PointOfInterestFormValues) => {
    setLoadingSubmit(true);
    const createdBy = localStorage.getItem("userId") || "";
    const poiData = {
      ...preparePOIData(data),
      createdBy,
    };

    const result = await createPointOfInterest(poiData);

    if (result.success) {
      showAlert({
        title: t("pointsOfInterest.alerts.success"),
        text: t("pointsOfInterest.alerts.createdText", { name: data.name }),
        icon: "success",
      });
      handleCloseAddModal();
      invalidatePOIs();
    } else {
      showAlert({
        title: t("pointsOfInterest.alerts.error"),
        text: result.error || t("pointsOfInterest.alerts.createError"),
        icon: "error",
      });
    }
    setLoadingSubmit(false);
  };

  const handleUpdatePOISubmit = async (data: PointOfInterestFormValues) => {
    if (!editPOI) return;
    setLoadingSubmit(true);

    const poiData = preparePOIData(data);

    const result = await updatePointOfInterest(editPOI.id, poiData);

    if (result.success) {
      showAlert({
        title: t("pointsOfInterest.alerts.success"),
        text: t("pointsOfInterest.alerts.updatedText", { name: data.name }),
        icon: "success",
      });
      handleCloseAddModal();
      invalidatePOIs();
    } else {
      showAlert({
        title: t("pointsOfInterest.alerts.error"),
        text: result.error || t("pointsOfInterest.alerts.updateError"),
        icon: "error",
      });
    }
    setLoadingSubmit(false);
  };

  const handleDeletePOI = (poi: PointOfInterest) => {
    showAlert({
      title: t("pointsOfInterest.alerts.deleteTitle", "¿Eliminar punto de interés?"),
      text: t("pointsOfInterest.alerts.deleteText", {
        name: poi.name || "",
        defaultValue: "Esta acción no se puede deshacer.",
      }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("pointsOfInterest.alerts.deleteConfirm", "Eliminar"),
      cancelButtonText: t("pointsOfInterest.alerts.cancel", "Cancelar"),
      confirmButtonColor: "#ef4444",
      onConfirm: async () => {
        const result = await deletePointOfInterest(poi.id);
        if (result.success) {
          invalidatePOIs();
          showAlert({
            title: t("pointsOfInterest.alerts.successTitle", "Éxito"),
            text: t("pointsOfInterest.alerts.deleteSuccess", "Punto de interés eliminado correctamente"),
            icon: "success",
          });
        } else {
          showAlert({
            title: t("pointsOfInterest.alerts.error"),
            text: result.error || t("pointsOfInterest.alerts.deleteError", "No se pudo eliminar"),
            icon: "error",
          });
        }
      },
    });
  };

  const handleSubmit = isEdit ? handleUpdatePOISubmit : handleCreatePOISubmit;

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className={`${themeClasses.bg} border-b ${themeClasses.border} px-4 py-3 sm:px-6 sm:py-4`}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 mb-3">
          <div className="min-w-0">
            <h1 className={`text-xl sm:text-2xl font-bold ${themeClasses.text}`}>
              {t("pointsOfInterest.title")}
            </h1>
            <p className={`text-sm ${themeClasses.textSecondary} mt-1`}>
              {t("pointsOfInterest.subtitle")}
            </p>
          </div>
          <CRUDTooltip className="self-start sm:self-auto flex-shrink-0">
            <button
              onClick={handleAddNew}
              disabled={disableCRUD}
              className="bg-primary-500 hover:bg-primary-400 text-white px-3 sm:px-4 py-2 rounded-lg font-medium flex items-center justify-center space-x-2 transition-colors whitespace-nowrap text-sm sm:text-base disabled:opacity-50"
            >
              <i className="ri-add-line"></i>
              <span>{t("pointsOfInterest.addPOI")}</span>
            </button>
          </CRUDTooltip>
        </div>

        <div className="flex items-center gap-2 min-w-0">
          <PointOfInterestFilters
            status_id={statusFilter}
            category_id={categoryFilter}
            searchQuery={searchTerm}
            isDarkMode={isDarkMode}
            themeClasses={themeClasses}
            onStatusChange={(value) => setStatusFilter(value || "")}
            onCategoryChange={(value) => setCategoryFilter(value || "")}
            onSearchChange={(e) => setSearchTerm(e.target.value)}
          />
          {/* View Toggle */}
          <div className="ml-auto flex shrink-0">
            <div
              className={`flex ${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-lg p-1`}
            >
              <button
                className={`px-3 py-1 rounded-md transition-colors ${
                  selectedView === "grid"
                    ? "bg-primary-500 text-gray-900"
                    : `${themeClasses.buttonText} ${themeClasses.buttonHover}`
                }`}
                onClick={() => setSelectedView("grid")}
              >
                <i className="ri-grid-fill"></i>
              </button>
              <button
                className={`px-3 py-1 rounded-md transition-colors ${
                  selectedView === "list"
                    ? "bg-primary-500 text-gray-900"
                    : `${themeClasses.buttonText} ${themeClasses.buttonHover}`
                }`}
                onClick={() => setSelectedView("list")}
              >
                <i className="ri-list-check"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden p-3 sm:p-6">
        {loading && (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
          </div>
        )}

        {error && (
          <div className="flex items-center justify-center h-64">
            <p className="text-red-500">{error}</p>
          </div>
        )}

        {!loading && !error && pointsOfInterest.length === 0 && (
          <div className="flex flex-col items-center justify-center h-64">
            <i className="ri-map-pin-line text-6xl text-gray-400 mb-4"></i>
            <p className={`${themeClasses.textSecondary}`}>
              {t("pointsOfInterest.noPOIs")}
            </p>
            <p className={`${themeClasses.textSecondary} text-sm mt-1`}>
              {t("pointsOfInterest.noPOIsDescription")}
            </p>
          </div>
        )}

        {!loading && !error && pointsOfInterest.length > 0 && (
          <>
            {selectedView === "grid" ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {pointsOfInterest.map((poi) => (
                  <PointOfInterestCard
                    key={poi.id}
                    poi={poi}
                    themeClasses={themeClasses}
                    isDarkMode={isDarkMode}
                    onEdit={handleEditPOI}
                    onDetails={handleDetailsClick}
                    onDelete={handleDeletePOI}
                  />
                ))}
              </div>
            ) : (
              <div className={DENSE_LIST}>
                {pointsOfInterest.map((poi) => (
                  <PointOfInterestRowCard
                    key={poi.id}
                    poi={poi}
                    themeClasses={themeClasses}
                    isDarkMode={isDarkMode}
                    onEdit={handleEditPOI}
                    onDetails={handleDetailsClick}
                    onDelete={handleDeletePOI}
                  />
                ))}
              </div>
            )}

            {totalPages > 1 && (
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            )}
          </>
        )}
      </div>

      {/* Modal de Detalles */}
      <PointOfInterestDetailsModal
        isOpen={isModalOpen}
        poi={selectedPOI}
        themeClasses={themeClasses}
        isDarkMode={isDarkMode}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedPOI(null);
        }}
        onEdit={(poi) => {
          setIsModalOpen(false);
          handleEditPOI(poi);
        }}
      />

      {/* Modal de Creación/Edición */}
      <PointOfInterestModal
        isOpen={showAddModal}
        isEdit={isEdit}
        loadingSubmit={loadingSubmit}
        form={form}
        themeClasses={themeClasses}
        isDarkMode={isDarkMode}
        mapCenter={mapCenter}
        markerPosition={markerPosition}
        hasRealChanges={hasRealChanges}
        onMarkerPositionChange={handleMarkerPositionChange}
        onCoordinateChange={handleCoordinateChange}
        onSubmit={handleSubmit}
        onClose={handleCloseAddModal}
      />
    </div>
  );
}
